import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useIntl} from "umi";
import {Select} from 'tea-component';

import {getEchartTooltipText} from "@/pages/dashboard/components/EchartsBubble";
import {
	getLegend,
	getPieSeries,
	getSeriesData,
	getXAxisData, sortChart
} from "@/pages/dashboard/pages/datalab/ReportAnalysis/untils/handleChartData";
import {numFormat, unitConversion} from "@/pages/dashboard/utils/utils";
import BarChart from "@/pages/dashboard/components/Chart/components/BarChart";
import LineChart from "@/pages/dashboard/components/Chart/components/LineChart";
import PieChart from "@/pages/dashboard/components/Chart/components/PieChart";
import Abnormal from "@/pages/dashboard/components/Abnormal";
import CardSlot from "@/pages/dashboard/components/CardSlot";
import moment from "moment";

function MetricSelect(props) {
	const {value, options = [], onChange, isEn} = props;
	
	return (<div className="metrics-select">
		<span>{isEn ? "Statistical Metric" : "统计指标"}</span>
		<Select
			appearance="button"
			options={options}
			value={value}
			onChange={value => {
				onChange(value);
			}
			}
			matchButtonWidth
			placeholder={isEn ? "Please select metric" : "请选择一个指标"}
		/>
	</div>);
}

function ReportChart(props: any = {}) {
	const {
		queries,
		loading,
		chartResponse,
		pieResponse,
		metrics,
		metricsEn,
		error,
		isOnlyShowPie,
		isEn,
		getDynamicLocalText,
		dimensionTypes,
	} = props;
	const {formatMessage} = useIntl();
	const [metricName, setMetricName] = useState(""); // 统计指标下拉选项的值
	const [metricText, setMetricText] = useState(""); // 由于有中英文切换, 所以metricText是统计指标显示的文本, 用于图表浮窗中的展示
	const [chartDatas, setChartDatas] = useState([])
	const [pieDatas, setPieDatas] = useState([])
	
	useEffect(() => {
		try {
			function getLocalName(arr: []) {
				return arr?.map((item: any) => {
					const newName = item?.dimension_name?.split(",")?.map((text, index) => getDynamicLocalText(dimensionTypes[index], text, isEn))?.join(",")
					return {
						...item,
						dimension_name: newName,
					}
				}) || []
			}
			if (!dimensionTypes) return;
			if (chartResponse) {
				const newChats = chartResponse?.map((chat) => {
					return getLocalName(chat);
				})
				setChartDatas(newChats)
			}
			if (pieResponse) {
				const newPies = pieResponse?.map(pie => {
					const newData = {};
					Object.keys(pie)?.forEach(key => newData[key] = getLocalName(pie[key]))
					return newData
				})
				setPieDatas(newPies)
			}
			
		} catch (e) {
			console.log("chat数据转换错误", e);
		}
	}, [dimensionTypes, chartResponse, pieResponse, isEn])
	
	
	const keyMetrics = useMemo(() => {
		setMetricName(metricName || metrics?.[0]);
		let newMetricText = metricText;
		const opts = metrics?.map?.((metric, index) => {
			if (metricName === metric) newMetricText = isEn ? metricsEn[index] : metric
			return {text: isEn ? metricsEn[index] : metric, value: metric}
		}) || [];
		setMetricText(newMetricText || opts[0]?.text)
		return opts;
	}, [metrics, isEn]);
	const {xAxisData, legend, hasContrast} = useMemo(() => {
		const xAxisData1 = getXAxisData(chartDatas?.[0]);
		const xAxisData2 = getXAxisData(chartDatas?.[1]);
		const legend = chartDatas?.map?.((chart, index) => {
			const legend = getLegend(chart, !!index) || {};
			legend.id = index;
			if (chartDatas.length === 1) {
				legend.bottom = "5";
				legend.left = 'auto';
				legend.width = '100%';
			}
			return legend;
		});
		return {xAxisData: [xAxisData1, xAxisData2], legend, hasContrast: chartDatas?.length > 1};
	}, [chartDatas]);
	
	
	const {barSeries, lineSeries} = useMemo(() => {
		if (!chartDatas || !metricName) return {barSeries: [], lineSeries: []};
		const lineSeries = [
			...getSeriesData(chartDatas[0], 'line', metricName, false),
			...getSeriesData(chartDatas[1], 'line', metricName, true)
		];
		
		const barSeries = [
			...getSeriesData(chartDatas[0], 'bar', metricName, false),
			...getSeriesData(chartDatas[1], 'bar', metricName, true)
		];
		return {barSeries, lineSeries};
		
	}, [metricName, chartDatas]);
	
	const {pieSeries, pieLegendData} = useMemo(() => {
		if (!pieDatas || !metricName) return {pieSeries: [], pieLegendData: []};
		let pieSeries = [];
		const pieLegendData = [];
		pieDatas?.forEach?.((data, index) => {
			const {series, legend} = getPieSeries(data, metricName, index);
			legend.id = index;
			// if (pieDatas.length === 1) {
			//   legend.bottom = "5";
			//   legend.left = 'auto';
			//   legend.width = '100%';
			//   legend.show = data?.[metricName]?.length > 1; // 当只有一个图例的时候, 不显示
			// }
			pieSeries.push(series);
			pieLegendData.push(legend);
		});
		return {
			pieSeries,
			pieLegendData,
		};
	}, [pieDatas, metricName]);
	
	
	const dateValue = useMemo(() => {
		return queries?.filters.find(item => item.column === "dtstatdate");
	}, [queries]);
	
	const [chartType, setChartType] = useState('column-chart');
	const iconList = useMemo(() => {
		if (dateValue) {
			setChartType('column-chart');
			return [
				{icon: 'column-chart', context: formatMessage({id: "common_bar_chart"})},
				{icon: 'discount-chart', context: formatMessage({id: "common_line_chart"})},
				{icon: 'pie-chart-icon', context: formatMessage({id: 'common_pie_chart'})},
			];
		} else {
			setChartType('pie-chart-icon');
			return [
				{icon: 'pie-chart-icon', context: formatMessage({id: 'common_pie_chart'})},
			];
		}
	}, [dateValue]);
	
	
	// 柱形图浮窗
	const barFormatterFunc = (params: any = []) => {
		const getTooltipData = (params, chartIndex) => {
			let title = xAxisData[chartIndex][params?.[0]?.dataIndex];
			const chartData = chartDatas[chartIndex];
			const data = params?.map((item) => {
				const source = chartData?.find(data => (data?.dimension_name === item?.seriesName?.trim()));
				return {
					name: item?.seriesName,
					metricsNum: unitConversion(item?.data),
					ratioNum: source?.metrics?.[item?.dataIndex]?.axisValues?.[metricName]?.percent || "-", // 百分比需要从原始数据获取
					color: item?.color,
				};
			});
			return {
				title,
				metricsName: metricText,
				ratioName: "Share",
				hasRatio: true,
				data,
				isColorDepth: true
			};
		};
		
		if (hasContrast) { // 如果有对比项
			// 先排序
			const [params1, params2] = sortChart(params?.slice?.(0, chartDatas[0].length), params?.slice?.(chartDatas[0].length, params?.length));
			return getEchartTooltipText({
				isSplit: hasContrast,
				splitData: [getTooltipData(params1, 0), getTooltipData(params2, 1)],
				isColorDepth: true,
			});
		} else {
			params?.sort((a, b) => {
				const aData = isNaN(a.data) ? -1 : Number(a.data);
				const bData = isNaN(b.data) ? -1 : Number(b.data);
				return bData - aData;
			});
			
			return getEchartTooltipText(getTooltipData(params, 0));
		}
		
	};
	
	const pieFormatterFunc = (params: any = {}) => {
		// 多时间筛选器展示（待优化）
		const time = dateValue?.value;
		let title = `${moment(time?.[0]).format("YYYY-MM-DD")}~${moment(time?.[1]).format("YYYY-MM-DD")}`;
		return getEchartTooltipText({
			title,
			hasRatio: false,
			data: [{
				name: params?.name,
				metricsNum: numFormat(params?.data?.value) || "-",
				color: params?.color,
			}],
			isColorDepth: true,
			seriesIndex: params.seriesIndex,
			isSplit: true
		});
	};
	
	const setOtherConfigFunc = (opt) => {
		opt.legend = legend;
		opt.xAxis = {...opt.xAxis[0], data: xAxisData[0]};
		// 判断一个图例时 不显示
		if (chartDatas?.length === 1 && chartDatas?.[0]?.length === 1) {
			opt.legend[0].show = false;
		}
		if (chartDatas?.length > 1) {
			opt.grid = {
				left: 10,
				right: 12,
				bottom: 56,
				top: 20,
				containLabel: true
			}
		}
		return opt;
	};
	
	const pieSetOtherConfigFunc = (opt) => {
		opt.legend = pieLegendData;
		if (pieSeries.length === 1) pieSeries[0].center = ['50%', '40%']
		opt.series = pieSeries
		return opt;
	}
	
	const chartMap: any = {
		'column-chart': {
			content: formatMessage({id: "common_bar_chart"}),
			component:
				(<BarChart
					height={315}
					title={''}
					// legendData={legend || []}
					xAxisData={xAxisData || []}
					series={barSeries || []}
					formatterFunc={barFormatterFunc}
					setOtherConfigFunc={setOtherConfigFunc}
				/>)
		},
		'discount-chart': {
			content: formatMessage({id: "common_line_chart"}),
			component:
				(<LineChart
					height={315}
					title={''}
					// legendData={legend || []}
					xAxisData={xAxisData || []}
					series={lineSeries || []}
					formatterFunc={barFormatterFunc}
					setOtherConfigFunc={setOtherConfigFunc}
				/>)
		},
		'pie-chart-icon': {
			content: formatMessage({id: 'common_pie_chart'}),
			component: (<PieChart
				height={309}
				isNodata={false}
				chartData={pieSeries || []} // pieSeries仅仅是用来更新饼图渲染, 真正的数据在pieSetOtherConfigFunc 方法中更新.
				// legendData={pieLegendData?.[0] || []}
				formatterFunc={pieFormatterFunc}
				setOtherConfigFunc={pieSetOtherConfigFunc}
			/>)
		}
	};
	
	return (
		<CardSlot
			getChange={(type: string) => setChartType(type)}
			title={(<MetricSelect
				value={metricName}
				options={keyMetrics}
				onChange={(value) => {
					setMetricName(value);
					setMetricText(keyMetrics.find(metric => metric?.value===value)?.text)
				}}
				isEn={isEn}
			/>)}
			iconList={iconList}
			activeTab={chartType}
			isEyes
			hiddenDownload
		>
			<div style={{position: "relative"}}>
				<Abnormal
					style={{height: '340px'}}
					component={chartMap?.[chartType]?.component}
					isLoading={loading}
					isError={error}
					isNoData={false}
				/>
				{chartDatas?.length > 1 && (
					<div
						style={{
							position: 'absolute',
							bottom: '0px',
							left: isEn ? '36px' : "45px",
							opacity: 0.45,
						}}
					>
						<p style={{ marginBottom: '8px', fontSize: '12px' }}>{isEn ? "Compare1" : `对比项1`}</p>
						<p style={{ fontSize: '12px' }}>{isEn ? "Compare2" : ` 对比项2`}</p>
					</div>
				)}
			</div>
		</CardSlot>
	);
}

export default ReportChart;
